import { mediaIdImageField } from '@/lib/mediaImageField';
import { GreaterAreaImageCell } from '@/components/greater-areas';
import { StateField } from '@/components/dropdowns';
import { Badge, type CrudConfig } from '@startupquickstart/frontend';

// Define type based on our Prisma schema
export interface GreaterArea {
  id: string;
  name: string;
  status: 'published' | 'draft' | 'needs_review';
  description?: string;
  featuredImageId?: string;
  city: string;
  state: string;
  country: string;
  latitude?: number;
  longitude?: number;
  createdAt: string;
  updatedAt: string;
  featuredImage?: {
    id: string;
    filename: string;
    name: string;
    mimeType: string;
    url?: string;
  };
  // Virtual field for large image display on detail pages
  featuredImageLarge?: {
    id: string;
    filename: string;
    name: string;
    mimeType: string;
    url?: string;
  };
}

/**
 * Greater Area CRUD configuration
 */
export const greaterAreaCrudConfig: CrudConfig<GreaterArea> = {
  entityName: 'Greater Area',
  entityNamePlural: 'Greater Areas',

  endpoints: {
    list: '/greater-areas',
    single: (id: string) => `/greater-areas/${id}`,
    create: '/greater-areas',
    update: (id: string) => `/greater-areas/${id}`,
    delete: (id: string) => `/greater-areas/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter greater area name',
      validation: {
        min: 2,
        max: 200,
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'published',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Needs Review', value: 'needs_review' },
      ],
      format: (value: string) => {
        if (!value) return null;
        const variant =
          value === 'published' ? 'default' :
          value === 'draft' ? 'secondary' :
          'warning';
        const label =
          value === 'published' ? 'Published' :
          value === 'draft' ? 'Draft' :
          value === 'needs_review' ? 'Needs Review' :
          value;
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
      validation: {
        max: 2000,
      },
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      ...mediaIdImageField,
      required: false,
      placeholder: 'Featured Image ID',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: GreaterArea['featuredImage'], fullRecord?: GreaterArea) => {
        return <GreaterAreaImageCell greaterArea={fullRecord} />;
      },
    },
    {
      key: 'featuredImageLarge',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: GreaterArea['featuredImage'], fullRecord?: GreaterArea) => {
        return <GreaterAreaImageCell greaterArea={fullRecord} size="lg" />;
      },
    },
    {
      key: 'country',
      label: 'Country',
      type: 'text',
      required: true,
      defaultValue: 'US',
      placeholder: 'Country',
    },
    {
      key: 'state',
      label: 'State',
      type: 'custom',
      required: true,
      placeholder: 'Select State',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
        formData?: Record<string, unknown>,
      ) => (
        <StateField
          value={value}
          onChange={onChange}
          placeholder={field.placeholder}
          country={formData?.country as string | undefined}
        />
      ),
    },
    {
      key: 'city',
      label: 'City',
      type: 'text',
      required: true,
      placeholder: 'City',
    },
    {
      key: 'latitude',
      label: 'Latitude',
      type: 'number',
      required: false,
      placeholder: 'Latitude',
    },
    {
      key: 'longitude',
      label: 'Longitude',
      type: 'number',
      required: false,
      placeholder: 'Longitude',
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['featuredImage', 'name', 'status', 'createdAt', 'updatedAt'],
    searchableFields: ['name', 'city', 'state', 'country'],
    sortableFields: ['name', 'status', 'city', 'state', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 10,
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'multiselect',
        options: [
          { label: 'Published', value: 'published' },
          { label: 'Draft', value: 'draft' },
          { label: 'Needs Review', value: 'needs_review' },
        ],
      },
      {
        key: 'featuredImageId',
        label: 'Featured Image',
        type: 'presence',
        presentLabel: 'Has Image',
        absentLabel: 'No Image',
      },
      {
        key: 'name',
        label: 'Name',
        type: 'text',
      },
      {
        key: 'city',
        label: 'City',
        type: 'text',
      },
      {
        key: 'state',
        label: 'State',
        type: 'text',
      },
      {
        key: 'country',
        label: 'Country',
        type: 'text',
      },
      {
        key: 'createdAt',
        label: 'Created At',
        type: 'daterange',
      },
      {
        key: 'updatedAt',
        label: 'Updated At',
        type: 'daterange',
      },
    ],
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core greater area details',
        fields: ['featuredImageLarge', 'name', 'status', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'Location Information',
        description: 'Geographic details',
        fields: ['city', 'state', 'country', 'latitude', 'longitude'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Greater Area',
        fields: ['featuredImageId', 'name', 'status', 'description'],
        columns: 1,
      },
      {
        title: 'Location',
        description: 'Where the Greater Area is located',
        fields: ['city', 'state', 'country'],
        columns: 2,
      },
      {
        title: 'Coordinates',
        description: 'Coordinates for the Greater Area',
        fields: ['latitude', 'longitude'],
        columns: 2,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 2,
        max: 200,
      },
      status: {
        required: true,
      },
      country: {
        required: true,
      },
      state: {
        required: true,
      },
      city: {
        required: true,
      },
    },
  },
};
